import { DEFAULT_CALENDAR, type CalendarConfig } from '@fsm/shared';

/** Pixels per hour on the board. 56 fits 07:00–19:00 on a laptop without scroll. */
export const HOUR_HEIGHT = 56;

export const BOARD: CalendarConfig = DEFAULT_CALENDAR;

/**
 * The hours a van is realistically out, used only to shade the rest.
 *
 * The board shows 07:00–19:00 because work sometimes happens there, but most
 * of a dispatcher's attention belongs between eight and six. Shading the edges
 * makes the working day the figure and the rest the ground, which is the
 * difference between reading a board and scanning one.
 */
export const WORKING_START_HOUR = 8;
export const WORKING_END_HOUR = 18;

export const HOURS: number[] = Array.from(
  { length: BOARD.dayEndHour - BOARD.dayStartHour + 1 },
  (_, i) => BOARD.dayStartHour + i,
);

export const BOARD_HEIGHT = (BOARD.dayEndHour - BOARD.dayStartHour) * HOUR_HEIGHT;

/** Minutes since the board's start hour -> pixels from the top. */
export function minutesToPx(minutes: number): number {
  return (minutes / 60) * HOUR_HEIGHT;
}

export function pxToMinutes(px: number): number {
  return (px / HOUR_HEIGHT) * 60;
}

/** Where a given time sits on the board, in pixels from the top. */
export function timeToOffset(date: Date): number {
  const minutes = date.getHours() * 60 + date.getMinutes() - BOARD.dayStartHour * 60;
  return minutesToPx(minutes);
}

/** Inverse: a pixel offset on `day` -> a snapped Date. */
export function offsetToTime(day: Date, offsetPx: number): Date {
  const minutes = BOARD.dayStartHour * 60 + pxToMinutes(offsetPx);
  const snapped = Math.round(minutes / BOARD.snapMinutes) * BOARD.snapMinutes;
  const out = new Date(day);
  out.setHours(0, 0, 0, 0);
  out.setMinutes(snapped);
  return out;
}

export function formatHourLabel(hour: number): string {
  if (hour === 12) return 'noon';
  return hour < 12 ? `${hour} am` : `${hour - 12} pm`;
}
